import React from 'react';
import { Layout } from '../../components/layout/Layout';
import { ScrollAnimation } from '../../components/common/ScrollAnimation';
import { Button } from '../../components/ui/Button';
import { Link } from 'react-router-dom';
import { Heart, Camera, Video, Sparkles, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

export const WeddingsPage: React.FC = () => {
  const weddingTypes = [
    { title: 'Outdoor Weddings', description: 'Beautiful outdoor wedding capturing nature and love', icon: MapPin, gradient: 'from-emerald-500 to-teal-600', shadow: 'shadow-emerald-400/30', bg: 'from-emerald-50 to-teal-50', border: 'border-emerald-200' },
    { title: 'Hindu Weddings', description: 'Complete Hindu wedding photography coverage', icon: Heart, gradient: 'from-rose-500 to-pink-600', shadow: 'shadow-rose-400/30', bg: 'from-rose-50 to-pink-50', border: 'border-rose-200' },
    { title: 'Brahmin Weddings', description: 'Traditional Brahmin wedding rituals and ceremonies', icon: Sparkles, gradient: 'from-amber-500 to-orange-600', shadow: 'shadow-amber-400/30', bg: 'from-amber-50 to-orange-50', border: 'border-amber-200' },
    { title: 'Engagement', description: 'Beautiful engagement ceremony photography', icon: Heart, gradient: 'from-violet-500 to-purple-600', shadow: 'shadow-violet-400/30', bg: 'from-violet-50 to-purple-50', border: 'border-violet-200' },
    { title: 'Sangeet', description: 'Energetic sangeet ceremony captures', icon: Video, gradient: 'from-cyan-500 to-blue-600', shadow: 'shadow-cyan-400/30', bg: 'from-cyan-50 to-blue-50', border: 'border-cyan-200' },
    { title: 'Muslim Weddings', description: 'Traditional Muslim wedding ceremonies', icon: Heart, gradient: 'from-primary-500 to-fuchsia-600', shadow: 'shadow-primary-400/30', bg: 'from-primary-50 to-fuchsia-50', border: 'border-primary-200' },
    { title: 'Christian Weddings', description: 'Elegant church weddings and ceremonies', icon: Heart, gradient: 'from-indigo-500 to-blue-600', shadow: 'shadow-indigo-400/30', bg: 'from-indigo-50 to-blue-50', border: 'border-indigo-200' },
    { title: 'Post-Wedding Shoot', description: 'Creative post-wedding couple portraits', icon: Camera, gradient: 'from-orange-500 to-red-600', shadow: 'shadow-orange-400/30', bg: 'from-orange-50 to-red-50', border: 'border-orange-200' },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section
        className="relative text-white py-20 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #881337 0%, #9d174d 40%, #701a75 70%, #4c1d95 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-72 h-72 rounded-full blur-3xl opacity-25" style={{ background: '#fda4af' }} />
          <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full blur-3xl opacity-20" style={{ background: '#f0abfc' }} />
        </div>
        <div className="h-1 absolute top-0 left-0 right-0 bg-gradient-to-r from-rose-400 via-amber-400 via-violet-400 to-cyan-400" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              Wedding{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, #fda4af, #fcd34d)' }}>
                Photography
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-pink-100 max-w-3xl mx-auto">
              Our wedding photographers think that taking pictures shouldn't take up much of your wedding day.
              Your wedding day should be the main subject of your photos.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Wedding Types */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #fdf4ff 0%, #fae8ff 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 gradient-text">Wedding Types We Cover</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              We specialize in various wedding styles and traditions
            </p>
          </ScrollAnimation>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {weddingTypes.map((type, index) => (
              <ScrollAnimation key={index} direction="up" delay={index * 0.08}>
                <motion.div
                  whileHover={{ y: -10, scale: 1.03 }}
                  className={`bg-gradient-to-br ${type.bg} p-6 rounded-2xl shadow-lg ${type.shadow} hover:shadow-2xl transition-all border ${type.border}`}
                >
                  <div className={`bg-gradient-to-br ${type.gradient} rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center shadow-lg ${type.shadow}`}>
                    <type.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-center text-gray-800">{type.title}</h3>
                  <p className="text-gray-600 text-center text-sm">{type.description}</p>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* Colorful divider */}
      <div className="h-1.5 w-full bg-gradient-to-r from-rose-500 via-amber-500 via-violet-500 to-emerald-500" />

      {/* What We Cover */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #ecfeff 0%, #fdf4ff 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 gradient-text">What We Cover</h2>
          </ScrollAnimation>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ScrollAnimation direction="right" delay={0.1}>
              <div className="bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-2xl font-semibold mb-4 text-rose-700">Complete Wedding Coverage</h3>
                <ul className="space-y-3 text-gray-700">
                  {[
                    'Bride and groom getting ready',
                    'Individual and couple portraits',
                    'Family portraits',
                    'All ceremonies and rituals',
                    'Candid moments throughout the day',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-rose-500 font-bold mt-0.5">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollAnimation>
            <ScrollAnimation direction="left" delay={0.2}>
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-2xl font-semibold mb-4 text-violet-700">Our Style</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Our techniques are participatory and hands-on, they let you and your guests be completely yourself.
                  We capture casual photos as well as some stunning staged shots.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We want to chronicle your wedding in the manner of contemporary wedding photography,
                  capturing emotions, genuine moments, and authentic memories.
                </p>
              </div>
            </ScrollAnimation>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #fdf4ff 0%, #fff1f2 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 gradient-text">Wedding Gallery</h2>
          </ScrollAnimation>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { id: '1617627143750-d86bc21e42bb', label: 'Bridal Portrait' },
              { id: '1597157639073-69284dc0fdaf', label: 'Mehndi & Rituals' },
              { id: '1587271407850-8d438ca9fdf2', label: 'Reception Stage' },
              { id: '1583939003579-730e3918a45a', label: 'Confetti Moment' },
              { id: '1465495976277-4387d4b0b4c6', label: 'Ring Ceremony' },
              { id: '1606216794074-735e91aa2c92', label: 'Just Married' },
              { id: '1523438885200-e635ba2c371e', label: 'Mandap Decor' },
              { id: '1621621667797-e06afc217fb0', label: 'Couple Portrait' },
            ].map((img, item) => (
              <ScrollAnimation key={img.id} direction="up" delay={item * 0.05}>
                <motion.div
                  whileHover={{ scale: 1.05, rotate: item % 2 === 0 ? 2 : -2 }}
                  className="aspect-square rounded-2xl overflow-hidden shadow-lg cursor-pointer group relative bg-rose-100"
                >
                  <img
                    src={`https://images.unsplash.com/photo-${img.id}?auto=format&fit=crop&w=500&q=80`}
                    alt={img.label}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-3 left-3 right-3 text-white text-xs font-semibold">{img.label}</div>
                  </div>
                </motion.div>
              </ScrollAnimation>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/gallery">
              <Button variant="primary" size="lg" className="shadow-lg shadow-primary-500/30">
                View Full Gallery
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #701a75 0%, #881337 50%, #7c2d12 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-full" style={{ background: 'linear-gradient(to br, rgba(255,255,255,0.05), transparent)' }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <ScrollAnimation direction="fade">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Book Your{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, #fcd34d, #fda4af)' }}>
                Wedding Photography
              </span>
            </h2>
            <p className="text-xl text-pink-100 mb-8 max-w-2xl mx-auto">
              Let us capture your special day with our expert wedding photography
            </p>
            <Link to="/contact">
              <Button variant="secondary" size="lg" className="shadow-2xl shadow-amber-500/30">
                Get In Touch
              </Button>
            </Link>
          </ScrollAnimation>
        </div>
      </section>
    </Layout>
  );
};
